import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { scopeTitle, attText, diaryText } from './complianceScope';

const SCHOOL_NAME = 'The Rising School';
const range = (r) => (r.from === r.to ? r.from : `${r.from} to ${r.to}`);

// jsPDF's built-in fonts have no emoji glyphs, so status is shown as coloured text.
const STATUS_COLOR = {
  Exemplary: [22, 128, 61],
  'Needs Improvement': [180, 110, 0],
  'Action Required': [185, 28, 28],
  Holiday: [100, 116, 139]
};

function drawHeader(doc, report, title, W, M) {
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(15, 23, 42);
  doc.text(SCHOOL_NAME, W / 2, 16, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(30, 64, 175);
  doc.text(title, W / 2, 24, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(71, 85, 105);
  const gen = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  doc.text(`Period: ${range(report)}   |   Generated: ${gen}`, W / 2, 30, { align: 'center' });
  doc.setDrawColor(203, 213, 225); doc.line(M, 34, W - M, 34);
}

function drawSummary(doc, report, total, W, M, boxY, boxH) {
  const s = report.summary || {};
  const boxW = W - 2 * M;
  doc.setFillColor(241, 245, 249); doc.setDrawColor(203, 213, 225);
  doc.roundedRect(M, boxY, boxW, boxH, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
  doc.text('EXECUTIVE SUMMARY', M + 4, boxY + 6);
  const cells = [
    ['Total Active Classes', s.totalActiveClasses ?? total, [15, 23, 42]],
    ['On-Time Submissions', s.onTimeSubmissions ?? 0, [22, 128, 61]],
    ['Delinquent / Pending', s.pending ?? 0, [185, 28, 28]]
  ];
  const cw = boxW / cells.length;
  cells.forEach(([label, val, col], i) => {
    const cx = M + cw * i + cw / 2;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...col);
    doc.text(String(val), cx, boxY + 15, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
    doc.text(label, cx, boxY + 19.5, { align: 'center' });
  });
}

/** Clean A4 print-ready PDF of the staff compliance report. */
export function downloadStaffCompliancePDF(report, scope = { includeAttendance: true, includeDiary: true }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 14;
  const rows = (report.rows || []).filter((r) => !r.holiday);
  const title = scopeTitle(report.timeframe, scope);
  const boxY = 38, boxH = 22;
  const daily = report.timeframe === 'daily';
  const onlyAtt = scope.includeAttendance && !scope.includeDiary;
  const onlyDiary = scope.includeDiary && !scope.includeAttendance;

  drawHeader(doc, report, title, W, M);
  drawSummary(doc, report, rows.length, W, M, boxY, boxH);

  let head, body;
  if (onlyAtt) {
    head = ['Class', 'Teacher Name', 'Attendance Status / Time', 'Punctuality'];
    if (!daily) head.push(`${report.timeframe === 'weekly' ? '7-Day ' : ''}Attendance Rate`);
    body = rows.map((r) => [r.classId, r.teacherName, attText(r, daily), r.punctuality, ...(daily ? [] : [`${r.attendanceRate}%`])]);
  } else if (onlyDiary) {
    head = ['Class', 'Teacher Name', 'Diary Status', 'Topics Logged'];
    if (!daily) head.push(`${report.timeframe === 'weekly' ? '7-Day ' : ''}Diary Rate`);
    body = rows.map((r) => [r.classId, r.teacherName, diaryText(r, daily), r.topicsLogged ?? 0, ...(daily ? [] : [`${r.diaryRate}%`])]);
  } else {
    head = ['Class', 'Teacher Name', 'Attendance Compliance (%)', 'Diary Compliance (%)', 'Punctuality (%)', 'Overall Status'];
    body = rows.map((r) => [r.classId, r.teacherName, `${r.attendanceRate}%`, `${r.diaryRate}%`, `${r.punctualityRate}%`, r.status]);
  }
  const statusCol = !onlyAtt && !onlyDiary ? 5 : -1;
  const columnStyles = { 0: { halign: 'center', cellWidth: 18 } };
  for (let i = 2; i < head.length; i++) columnStyles[i] = { halign: 'center' };

  autoTable(doc, {
    startY: boxY + boxH + 6,
    margin: { left: M, right: M, bottom: 20 },
    head: [head],
    body,
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 2.2, textColor: [15, 23, 42], lineColor: [226, 232, 240], lineWidth: 0.1 },
    headStyles: { fillColor: [30, 41, 59], textColor: 255, halign: 'center', fontSize: 8.5 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles,
    didParseCell: (d) => {
      if (d.section !== 'body') return;
      if (d.column.index === statusCol && STATUS_COLOR[d.cell.raw]) d.cell.styles.textColor = STATUS_COLOR[d.cell.raw];
      if (statusCol === -1 && d.column.index === 2) {
        const v = String(d.cell.raw);
        if (v === 'Pending') d.cell.styles.textColor = [185, 28, 28];
      }
    },
    didDrawPage: () => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text(`${SCHOOL_NAME} - ${title}`, M, H - 8);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - M, H - 8, { align: 'right' });
    }
  });

  let y = doc.lastAutoTable.finalY + 8;
  if (y > H - 45) { doc.addPage(); y = 25; }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
  doc.text('Status key: Exemplary (avg >= 90%) | Needs Improvement (70-89%) | Action Required (< 70%). Sundays and holidays excluded.', M, y);
  y += 22;
  doc.setDrawColor(15, 23, 42); doc.line(W - M - 60, y, W - M, y);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
  doc.text('Principal Signature', W - M - 30, y + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.text('Date: ____________', M, y + 5);

  const tag = onlyAtt ? 'attendance' : onlyDiary ? 'diary' : 'compliance';
  doc.save(`staff-${report.timeframe}-${tag}_${report.to}.pdf`);
}

// Backwards-compatible alias (both sections)
export const downloadCompliancePdf = (report) => downloadStaffCompliancePDF(report);
